import { useEffect, useState } from 'react';
import { IonLoading } from '@ionic/react';
import { accessibilityService } from '../services/AccessibilityService';

/**
 * This component handles the multi-page accessibility scan.
 * It checks if a scan is in progress on mount, and if so,
 * runs the scan on the current page and navigates to the next.
 */
const AccessibilityScanHandler: React.FC = () => {
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check if we have the scan query param
    const urlParams = new URLSearchParams(window.location.search);
    const isScanPage = urlParams.get('a11y-scan') === 'true';

    if (!isScanPage) {
      return;
    }

    // Check if there's a scan in progress
    const scanState = accessibilityService.getScanState();
    if (!scanState) {
      // No scan in progress, redirect to accessibility page
      window.location.href = '/accessibility';
      return;
    }

    const runScan = async () => {
      setScanning(true);
      const currentRoute = scanState.routes[scanState.currentIndex];
      setMessage(`Scanning ${currentRoute.name} (${scanState.currentIndex + 1}/${scanState.routes.length})...`);

      // Wait a bit for the page to fully render
      await new Promise(r => setTimeout(r, 1500));

      try {
        const result = await accessibilityService.continueMultiPageScan();

        if (result.done) {
          // All done - redirect to accessibility page to show results
          window.location.href = '/accessibility';
        }
        // If not done, the service will handle navigation to the next page
      } catch (error) {
        console.error('Accessibility scan error:', error);
        // Clear state and go back to accessibility page
        accessibilityService.clearScanState();
        window.location.href = '/accessibility';
      }
    };

    runScan();
  }, []);

  if (!scanning) {
    return null;
  }

  return (
    <IonLoading
      isOpen={scanning}
      message={message}
    />
  );
};

export default AccessibilityScanHandler;
